import { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Paper, Card, CardContent, 
  CardActions, Button, Chip, Grid, CircularProgress,
  Divider
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { useNavigate } from 'react-router-dom';
import { getDealReviewQueue } from '../api/crmClient';

interface DealReview {
  deal_id: string;
  name: string;
  amount: number;
  stage: string;
  owner: string;
  risk_factors: string[];
  coaching_tip: string;
}

export function CoachsCornerPage() {
  const [loading, setLoading] = useState(true);
  const [reviewQueue, setReviewQueue] = useState<DealReview[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchReviewQueue = async () => {
      try {
        setLoading(true);
        const data = await getDealReviewQueue();
        setReviewQueue(data);
      } catch (error) {
        console.error('Error fetching review queue:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchReviewQueue();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getRiskColor = (riskFactor: string) => {
    if (riskFactor.includes('Stalled')) return 'error';
    if (riskFactor.includes('Negative')) return 'error';
    if (riskFactor.includes('High Value')) return 'warning';
    if (riskFactor.includes('Low Engagement')) return 'warning';
    return 'default';
  };

  const handleGoToDealRoom = (dealId: string) => {
    navigate(`/deals/${dealId}`);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Coach's Corner
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ mb: 4 }}>
        AI-identified deals that need your attention and coaching
      </Typography>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : reviewQueue.length > 0 ? (
        <Grid container spacing={3}>
          {reviewQueue.map((deal) => (
            <Grid item xs={12} key={deal.deal_id}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="h6">{deal.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {deal.owner} • {deal.stage} • {formatCurrency(deal.amount)}
                      </Typography>
                    </Box>
                    <Box>
                      {deal.risk_factors.map((risk, index) => (
                        <Chip 
                          key={index} 
                          label={risk} 
                          size="small" 
                          color={getRiskColor(risk) as any}
                          icon={<WarningIcon />}
                          sx={{ ml: 1, mb: 1 }} 
                        />
                      ))}
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 2 }}>
                    <LightbulbIcon color="primary" sx={{ mr: 1, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" color="primary">
                        Coach's Tip:
                      </Typography>
                      <Typography variant="body2">
                        {deal.coaching_tip}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button 
                    variant="contained" 
                    size="small" 
                    onClick={() => handleGoToDealRoom(deal.deal_id)}
                  >
                    Go to Deal Room
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            No deals currently need your attention. Great job!
          </Typography>
        </Paper>
      )}
    </Container>
  );
} 